import { Button, Col, Divider, Row, Select, Tag } from "antd";
import { useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import TrademarkContext from "../../../context/trademark/context";
import TrademarkContextProvider from "../../../context/trademark/provider";
import { MediumFor } from "../../../models/medium/enum";
import { IUpdateTrademark } from "../../../models/trademark/request";
import { languages } from "../../../utils/helpers/constants";
import { getTranslations } from "../../../utils/helpers/translations";
import Controller from "../../form-components/controller";
import FieldBuilder from "../../form-components/field-builder";
import ImageUploader from "../../form-components/image-uploader-client";
import Spin from "../../general/antd/spin";
import FormItem from "../../general/form-item";
import { useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { ITag } from "../../../models/tag/response";
import styles from "./style.module.scss";
import { execute } from "../../../utils/helpers/execute";
import EndPoints from "../../../services/end-points";
import AppContext from "../../../context/app/context";
import Image from "../../general/antd/image";
import { errorNotification, successNotification } from "../../../utils/helpers/notification";

const Form = () => {
    const { t } = useTranslation();
    const { details, actions, loading } = useContext(TrademarkContext);
    const { id } = useParams();
    const [tags, setTags] = useState<ITag[]>([]);
    const [tagsLoading, setTagsLoading] = useState(true);
    const [tagKeyword, setTagKeyword] = useState<string>();
    const navigate = useNavigate();
    const { direction } = useContext(AppContext);

    const [newImage, setNewImage] = useState<string | null>(null);
    const [imageChanged, setImageChanged] = useState(false);
    const [imageLoading, setImageLoading] = useState<boolean>(false);

    const [newLogo, setNewLogo] = useState<string | null>(null);
    const [logoChanged, setLogoChanged] = useState(false);
    const [logoLoading, setLogoLoading] = useState<boolean>(false);

    const handleUploadNewImage = async (isLogo) => {
        if (!newImage && !newLogo) {
            errorNotification(t("no_new_image_to_upload"));
            return;
        }
        isLogo ? setLogoLoading(true) : setImageLoading(true)

        const formData = new FormData();
        try {
            const response = await fetch(isLogo ? newLogo : newImage);
            const blob = await response.blob();
            const file = new File([blob], "image.png", { type: "image/png" });

            formData.append("medium", file);
            formData.append("type", "1");
            formData.append("for", MediumFor.UserProfile);
            isLogo ? formData.append('without_background', withoutBackgroundLogo) : formData.append('without_background', withoutBackground);

            const uploadResponse = await EndPoints.media.uploadMedia(formData);
            isLogo ? setValue("logoId", uploadResponse.id, { shouldDirty: true }) : setValue("imageId", uploadResponse.id, { shouldDirty: true });

            successNotification(t("image_uploaded_successfully"));
        } catch (error) {
            errorNotification(t("failed_to_upload_image"));
        } finally {
            isLogo ? setLogoLoading(false) : setImageLoading(false)
        }
    };

    const Option = Select.Option;
    const methods = useForm<any>({
        defaultValues: {
            name: getTranslations(details?.translations?.name),
            note: getTranslations(details?.translations?.note),
            imageId: details?.image?.id ?? undefined,
            logoId: details?.logo?.id ?? undefined,
            isNew: details?.isNew ?? false,
            exportPurchaseInvoiceReport: details?.exportPurchaseInvoiceReport ?? false,
            hasOffers: details?.hasOffers ?? false,
            isSpecial: details?.isNew ?? false,
            tagIds: details?.tags?.map((tag) => tag?.id),
            order: details?.order,
            minOrderValueForFreeDelivery: details?.minOrderValueForFreeDelivery,
            maxOrderValueForFreeDelivery: details?.maxOrderValueForFreeDelivery,
            acceptedExchange: details?.acceptedExchange ?? false,
            without_background: details?.image?.without_background ?? false,
            without_background_logo: details?.logo?.without_background ?? false,

        } as IUpdateTrademark,
    });

    const {
        handleSubmit,
        reset,
        control,
        setValue,
    } = methods;

    useEffect(() => {
        reset({
            name: getTranslations(details?.translations?.name),
            note: getTranslations(details?.translations?.note),
            imageId: details?.image?.id ?? undefined,
            logoId: details?.logo?.id ?? undefined,
            isNew: details?.isNew ?? false,
            exportPurchaseInvoiceReport: details?.exportPurchaseInvoiceReport ?? false,
            hasOffers: details?.hasOffers ?? false,
            isSpecial: details?.isNew ?? false,
            tagIds: details?.tags?.map((tag) => tag?.id),
            order: details?.order,
            isOfferingFreeDelivery: details?.isOfferingFreeDelivery,
            minOrderValueForFreeDelivery: details?.minOrderValueForFreeDelivery,
            maxOrderValueForFreeDelivery: details?.maxOrderValueForFreeDelivery,
            acceptedExchange: details?.acceptedExchange ?? false,
            without_background: details?.image?.without_background ?? false,
            without_background_logo: details?.logo?.without_background ?? false,
        } as IUpdateTrademark);
    }, [details]);

    useEffect(() => {
        id && actions.getDetails(Number(id));
    }, [id]);

    useEffect(() => {
        const getTags = async () => {
            try {
                const { data } = await EndPoints.tag.getAllTags({
                    light: true,
                    perPage: -1,
                });
                setTags(data.data);
            } catch (error) {
            } finally {
                setTagsLoading(false);
            }
        };
        getTags();
    }, [direction]);

    const [loadingSubmit, setLoadingSubmit] = useState(false);
    const onSubmit = async (data: any) => {
        await execute({
            callback: async () => {
                setLoadingSubmit(true);
                details
                    ? await actions.updateTrademark(details?.id, data)
                    : await actions.createTrademark(data);
                navigate(-1);
            },
            fallback: (error) => {
            },
            finallyCallback: () => {
                setLoadingSubmit(false);
            },
            throwException: false,
        });
    };

    const currentImageId = useWatch({ control, name: "imageId" });

    useEffect(() => {
        if (details?.image?.id !== currentImageId) {
            setImageChanged(true);
        } else {
            setImageChanged(false);
        }
    }, [currentImageId, details?.image?.id]);

    const currentLogoId = useWatch({ control, name: "logoId" });

    useEffect(() => {
        if (details?.logo?.id !== currentLogoId) {
            setLogoChanged(true);
        } else {
            setLogoChanged(false);
        }
    }, [currentLogoId, details?.logo?.id]);

    const withoutBackground = useWatch({ control, name: "without_background" });
    const withoutBackgroundLogo = useWatch({ control, name: "without_background_logo" });
    return (
        <FormProvider {...methods}>
            <form className={styles.container} onSubmit={handleSubmit(onSubmit)}>
                <div>
                    <span className={styles.title}>
                        {details ? t("update_trademark") : t("add_trademark")}
                    </span>
                    <Divider className={styles.divider} />
                    {loading.includes("details") ? (
                        <div style={{ height: "60vh" }}>
                            <Spin />
                        </div>
                    ) : (
                        <div className={styles.children}>
                            <Row gutter={[16, 8]}>
                                {languages?.map((lang, index) => {
                                    return (
                                        <Col span={12}>
                                            <FieldBuilder
                                                key={index}
                                                name={`name[${lang?.code}]`}
                                                label={`${t("name")} (${lang?.name})`}
                                                input={{ type: "text" }}
                                                rules={{ required: true }}
                                            />
                                        </Col>
                                    );
                                })}
                                <Col xs={24} sm={24} md={8} lg={6}>
                                    <FieldBuilder
                                        name="order"
                                        label={t("order")}
                                        input={{ type: "number" }}
                                        rules={{ required: true }}
                                    />
                                </Col>
                                <Col xs={24} sm={24} md={8} lg={6}>
                                    <FieldBuilder
                                        name="minOrderValueForFreeDelivery"
                                        label={t("min_order_value_for_free_delivery")}
                                        input={{ type: "number" }}
                                        rules={{ required: false }}
                                    />
                                </Col>
                                <Col xs={24} sm={24} md={8} lg={6}>
                                    <FieldBuilder
                                        name="maxOrderValueForFreeDelivery"
                                        label={t("max_order_value_for_free_delivery")}
                                        input={{ type: "number" }}
                                        rules={{ required: false }}
                                    />
                                </Col>
                                <Col xs={24} sm={24} md={8} lg={6}>
                                    <FormItem label={t("tags")} required>
                                        <Controller
                                            name="tagIds"
                                            control={control}
                                            rules={{ required: true }}
                                            render={({ field: { ...field } }) => {
                                                return (
                                                    <Select
                                                        loading={tagsLoading}
                                                        {...field}
                                                        onSearch={(val) => {
                                                            setTagKeyword(val);
                                                        }}
                                                        style={{ width: "100%" }}
                                                        allowClear
                                                        mode="multiple"
                                                        placeholder={t("tags")}
                                                        filterOption={false}
                                                    >
                                                        {tags
                                                            .filter((tr) =>
                                                                tagKeyword
                                                                    ? tr?.name?.includes(tagKeyword)
                                                                    : true
                                                            )
                                                            .map((tr, index) => {
                                                                return (
                                                                    <Option
                                                                        key={index}
                                                                        value={tr?.id}
                                                                        label={
                                                                            <Tag color={tr?.color}>{tr.name}</Tag>
                                                                        }
                                                                    >
                                                                        <Tag color={tr?.color}>{tr?.name}</Tag>
                                                                    </Option>
                                                                );
                                                            })}
                                                    </Select>
                                                );
                                            }}
                                        />
                                    </FormItem>
                                </Col>
                                <Col sm={0} md={12} lg={0}>
                                </Col>
                                <Col span={6}>
                                    <FieldBuilder
                                        label={`${t("is_new")}`}
                                        name="isNew"
                                        input={{ type: "checkBox" }}
                                    />
                                </Col>
                                <Col span={6}>
                                    <FieldBuilder
                                        label={`${t("export_purchase_invoice_report")}`}
                                        name="exportPurchaseInvoiceReport"
                                        input={{ type: "checkBox" }}
                                    />
                                </Col>
                                {details && (
                                    <Col span={6}>
                                        <FieldBuilder
                                            label={`${t("free_delivery")}`}
                                            name="isOfferingFreeDelivery"
                                            input={{ type: "checkBox" }}
                                        />
                                    </Col>
                                )}
                                <Col span={6}>
                                    <FieldBuilder
                                        label={`${t("accepted_exchange")}`}
                                        name="acceptedExchange"
                                        input={{ type: "checkBox" }}
                                    />
                                </Col>
                                {details && (
                                    <Col span={6}>
                                        <FieldBuilder
                                            label={`${t("has_offers")}`}
                                            name="hasOffers"
                                            input={{ type: "checkBox" }}
                                        />
                                    </Col>
                                )}
                                {languages?.map((lang, index) => {
                                    return (
                                        <Col span={12}>
                                            <FieldBuilder
                                                key={index}
                                                name={`note[${lang?.code}]`}
                                                label={`${t("note")} (${lang?.name})`}
                                                input={{ type: "text-area" }}
                                                rules={{ required: false }}
                                            />
                                        </Col>
                                    );
                                })}
                                <Col span={24}>
                                    <FormItem label={t("image")} required>
                                        <Controller
                                            name="imageId"
                                            rules={{
                                                required: { value: true, message: `${t("field_is_required_message")}` },
                                            }}
                                            render={({ field: { ref, ...field } }) => {
                                                return (
                                                    <ImageUploader
                                                        {...field}
                                                        type="DRAGGER"
                                                        imageFor={MediumFor.UserProfile}
                                                        onChange={(response) => {
                                                            if (response) {
                                                                setNewImage(response.url);
                                                            } else {
                                                                setNewImage(null);
                                                            }
                                                            field.onChange(response?.id);
                                                        }}
                                                    />
                                                );
                                            }}
                                        />
                                    </FormItem>
                                </Col>
                                <div style={{ display: "flex", gap: 32, flexWrap: "wrap" }}>
                                    {details?.image && (
                                        <div style={{ textAlign: "center", flex: "1 1 300px" }}>
                                            <h4>{t("old_image")}</h4>
                                            <Image src={details.image.url} preview={true} style={{ width: "100%", maxHeight: 300 }} />
                                        </div>
                                    )}

                                    {newImage && (
                                        <div style={{ textAlign: "center", flex: "1 1 300px" }}>
                                            <h4>{t("new_image")}</h4>
                                            <Image src={newImage} preview={true} style={{ width: "100%", maxHeight: 300 }} />
                                        </div>
                                    )}
                                </div>
                                <Col span={24}>
                                    <FieldBuilder
                                        label={`${t("without_background")}`}
                                        name="without_background"
                                        input={{ type: "checkBox" }}
                                    />
                                </Col>
                                {imageChanged && (
                                    <Button type="primary" onClick={() => handleUploadNewImage(false)} style={{ marginTop: 16 }} loading={imageLoading}>
                                        {t("upload_new_image")}
                                    </Button>
                                )}
                                {details?.image && !imageChanged && (
                                    <Button
                                        type="default"
                                        onClick={async () => {
                                            try {
                                                await EndPoints.media.updateMedia(details.image.id, Boolean(withoutBackground));
                                                successNotification(t("background_type_updated_successfully"));
                                            } catch (error) {
                                                errorNotification(t("failed_to_update_background_type"));
                                            }
                                        }}
                                        style={{ marginTop: 16 }}
                                    >
                                        {t("update_image_kind")}
                                    </Button>
                                )}

                                <Col span={24}>
                                    <FormItem label={t("logo")} required>
                                        <Controller
                                            name="logoId"
                                            rules={{
                                                required: { value: true, message: `${t("field_is_required_message")}` },
                                            }}
                                            render={({ field: { ref, ...field } }) => {
                                                return (
                                                    <ImageUploader
                                                        {...field}
                                                        type="DRAGGER"
                                                        imageFor={MediumFor.UserProfile}
                                                        onChange={(response) => {
                                                            if (response) {
                                                                setNewLogo(response.url);
                                                            } else {
                                                                setNewLogo(null);
                                                            }
                                                            field.onChange(response?.id);
                                                        }}
                                                    />
                                                );
                                            }}
                                        />
                                    </FormItem>
                                </Col>
                                <div style={{ display: "flex", gap: 32, flexWrap: "wrap" }}>
                                    {details?.logo && (
                                        <div style={{ textAlign: "center", flex: "1 1 300px" }}>
                                            <h4>{t("old_image")}</h4>
                                            <Image src={details.logo.url} preview={true} style={{ width: "100%", maxHeight: 300 }} />
                                        </div>
                                    )}

                                    {newLogo && (
                                        <div style={{ textAlign: "center", flex: "1 1 300px" }}>
                                            <h4>{t("new_image")}</h4>
                                            <Image src={newLogo} preview={true} style={{ width: "100%", maxHeight: 300 }} />
                                        </div>
                                    )}
                                </div>
                                <Col span={24}>
                                    <FieldBuilder
                                        label={`${t("without_background")}`}
                                        name="without_background_logo"
                                        input={{ type: "checkBox" }}
                                    />
                                </Col>
                                {logoChanged && (
                                    <Button type="primary" onClick={() => handleUploadNewImage(true)} style={{ marginTop: 16 }} loading={logoLoading}>
                                        {t("upload_new_image")}
                                    </Button>
                                )}
                                {details?.logo && !logoChanged && (
                                    <Button
                                        type="default"
                                        onClick={async () => {
                                            try {
                                                await EndPoints.media.updateMedia(details.logo.id, Boolean(withoutBackgroundLogo));
                                                successNotification(t("background_type_updated_successfully"));
                                            } catch (error) {
                                                errorNotification(t("failed_to_update_background_type"));
                                            }
                                        }}
                                        style={{ marginTop: 16 }}
                                    >
                                        {t("update_image_kind")}
                                    </Button>
                                )}
                            </Row>
                        </div>
                    )}
                </div>

                <div>
                    <Divider className={styles.divider} />
                    <div className={styles.formFooter}>
                        <Button
                            onClick={() => {
                                navigate(-1);
                            }}
                        >
                            {t("cancel")}
                        </Button>
                        <Button loading={loadingSubmit} htmlType="submit" type="primary">
                            {t("save")}
                        </Button>
                    </div>
                </div>
            </form>
        </FormProvider>
    );
};

const TrademarkForm = () => {
    return (
        <TrademarkContextProvider>
            <Form />
        </TrademarkContextProvider>
    );
};
export default TrademarkForm;
